import PropTypes from "prop-types";
import React from "react";
import { Container, Row, Col, Form, Alert } from "reactstrap";
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from "@headlessui/react";

//i18n
import { withTranslation } from "react-i18next";

// Global Components
import DateRangePicker from "components/DateRangePicker";
import Breadcrumbs from "components/Common/Breadcrumb";

// Local Components
import StatsCard from "./components/StatsCard";
import AffiliateTab from "./AffiliateTab";
import ProductTab from "./ProductTab";

// Validation
import { useFormik } from "formik";
import { DateRangeSchema } from "model/_base/stats/stats.schema";

// API
import { useGetDashboardStatsQuery } from "services/dashboard/api";

// Icons
import user from "assets/icons/user.svg";
import flag from "assets/icons/flag.svg";
import coins from "assets/icons/coins-stacked.svg";
import cursor from "assets/icons/cursor-click.svg";
import link from "assets/icons/link.svg";

const Icons = [user, flag, cursor, coins];

const Dashboard = (props) => {
  // TITLE
  document.title = `${props.t("Dashboard")} | ${process.env.REACT_APP_CMS_TITLE}`;

  // FORM
  const formik = useFormik({
    initialValues: {
      dateRange: {
        // Default to yesterday for both start and end
        startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
        endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      },
    },
    validationSchema: DateRangeSchema,
  });

  // API
  const { data, error, isLoading } = useGetDashboardStatsQuery(formik.values);

  // RENDER
  return (
    <div className="page-content" data-testid="dashboard-page">
      <Container fluid>
        <Breadcrumbs title={props.t("Dashboards")} breadcrumbItem={props.t("Dashboard")} />

        <div className="d-flex flex-column gap-4">
          <div>
            <Row className="mb-4">
              <Col sm={12} md={4} lg={4}>
                <div className="datepicker">
                  <DateRangePicker
                    value={formik.values.dateRange || { startDate: null, endDate: null }}
                    onChange={(newValue) => {
                      formik.setFieldValue("dateRange", newValue, false);
                    }}
                    error={formik.touched.dateRange && formik.errors.dateRange}
                    useRange={true}
                    // Disallow selecting today or future; only up to yesterday
                    maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              {isLoading ? (
                <>
                  <Col xl={8}>
                    <Row>
                      {[...Array(4)].map((_, index) => (
                        <Col md={6} xs={12} key={`loading-${index}`} className="mb-4">
                          <StatsCard id={`loading-${index}`} title="Loading..." isLoading={true} />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                  <Col xl={4}>
                    <Row>
                      {[...Array(2)].map((_, index) => (
                        <Col md={12} key={`loading-affiliate-${index}`} className="mb-2">
                          <StatsCard id={`loading-affiliate-${index}`} title="Loading..." isLoading={true} />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </>
              ) : error ? (
                <Alert color="danger">{error.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}</Alert>
              ) : (
                <>
                  {data && (
                    <>
                      <Col xl={8}>
                        <Row>
                          <Col md={6} xs={12} className="mb-4">
                            <StatsCard id="partners" icon={user} title="พาร์ทเนอร์ทั้งหมด" value={[data.current.partners]} change={[data.comparison.partners.percentage]} isLoading={isLoading} />
                          </Col>
                          <Col md={6} xs={12} className="mb-4">
                            <StatsCard id="campaigns" icon={flag} title="รายการแคมเปญสินค้าที่มีในระบบ" value={[data.current.campaigns]} change={[data.comparison.campaigns.percentage]} isLoading={isLoading} />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6} xs={12} className="mb-4">
                            <StatsCard id="clicks" icon={cursor} title="จำนวนการ Click ทั้งหมด" value={[data.current.clicks]} change={[data.comparison.clicks.percentage]} isLoading={isLoading} />
                          </Col>
                          <Col md={6} xs={12} className="mb-4">
                            <StatsCard id="links" icon={link} title="จำนวนลิงค์ที่ถูกสร้างแล้ว" value={[data.current.links]} change={[data.comparison.links.percentage]} isLoading={isLoading} />
                          </Col>
                        </Row>
                      </Col>
                      <Col xl={4}>
                        <Row>
                          <Col xs={12} className="mb-4">
                            <StatsCard id="affiliate" icon={coins} title="รายได้จาก Affiliate" value={[data.current.sales, data.current.earnings]} change={[data.comparison.sales.percentage, data.comparison.earnings.percentage]} subtitle={["รายได้จาก Affiliate", "คอมมิชชันของ Affiliate"]} isLoading={isLoading} />
                          </Col>
                        </Row>
                      </Col>
                    </>
                  )}
                </>
              )}
            </Row>
          </div>
          <TabGroup>
            <TabList>
              <Tab>Affiliate</Tab>
              <Tab>Product</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <AffiliateTab dateRange={formik.values.dateRange} />
              </TabPanel>
              <TabPanel>
                <ProductTab dateRange={formik.values.dateRange} />
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </Container>
    </div>
  );
};

Dashboard.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(Dashboard);
